import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  useSortable,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

import {
  Box,
  Typography,
  IconButton,
  Checkbox,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput,
  TextField,
  InputAdornment,
  Tooltip,
  Snackbar,
  Alert,
  Button,
  Stack,
  AppBar,
  Toolbar,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  DragIndicator,
  SearchOutlined,
  ClearOutlined,
  EditOutlined,
  PlayArrow,
} from "@mui/icons-material";
import { ReactComponent as NounLink } from "../../../assets/noun-link.svg";
import logo from "../../../assets/ca-logo.png";
import { jsonParser } from "../../../utils/helpers";
import DialogPaper from "../../../partials/dialogPaper";

import Thumbnail from "./thumbnail";
import SlidePreview from "./slidePreview";

export default function SlideManagement(props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const presentationJson = useMemo(
    () => jsonParser(props.presentation?.json),
    [props.presentation?.json],
  );

  const [slides, setSlides] = useState(props.presentation?.slides || []);
  const [snapshot, setSnapshot] = useState(null);
  const [selectedSlides, setSelectedSlides] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [showMoveSlides, setShowMoveSlides] = useState(false);
  const [showDuplicateSlides, setShowDuplicateSlides] = useState(false);
  const [showPublishSlides, setShowPublishSlides] = useState(false);
  const [showAddSlide, setShowAddSlide] = useState(false);
  const [addPosition, setAddPosition] = useState("");
  const [numberOfDuplicates, setNumberOfDuplicates] = useState(1);
  const [showSlideDuplicateError, setSlideDuplicateError] = useState(false);
  const [slidesPosition, setSlidesPosition] = useState("");
  const [moveTarget, setMoveTarget] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const pendingActionRef = useRef(null);
  const selectedAction = "";

  // Number of slide-preview dialogs currently open across the grid. While any
  // preview is open we disable drag-reordering so slides can't be moved
  // "underneath" the preview backdrop.
  const [previewOpenCount, setPreviewOpenCount] = useState(0);
  const handlePreviewOpenChange = useCallback((isOpen) => {
    setPreviewOpenCount((count) => Math.max(0, count + (isOpen ? 1 : -1)));
  }, []);

  // Defer mounting the (heavy) sortable grid until after the modal paints,
  // so the user sees the dialog open immediately instead of staring at a
  // frozen tab while N cards spin up.
  const [gridReady, setGridReady] = useState(false);

  // The parent (editPresentation) re-renders constantly during window resize
  // because it subscribes to the resize event for an unrelated overlay. Stash
  // the latest props in a ref so per-card callbacks can stay referentially
  // stable across those re-renders and React.memo on the cards holds.
  const propsRef = useRef(props);
  propsRef.current = props;

  // Snapshot the upstream slides on open so we can diff against local edits to
  // know when the deck is dirty. While the modal is closed, keep local state
  // in sync with the upstream presentation.
  useEffect(() => {
    if (props.open) {
      const fresh = props.presentation?.slides || [];
      setSlides(fresh);
      setSnapshot(
        fresh.map((s) => ({ id: s.id, title: s.title || "" })),
      );
    } else {
      setSnapshot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    if (props.open) return;
    setSlides(props.presentation?.slides || []);
  }, [props.open, props.presentation?.slides]);

  // While open, reconcile local view with upstream when an Actions menu
  // operation (delete / move / duplicate / toggle hide) updates the parent
  // presentation. Always pull fresh field values (e.g. `published`) and
  // preserve in-progress title edits for slides that still exist. If the
  // user has a pending drag-reorder, keep the local order and just merge
  // field updates from upstream.
  useEffect(() => {
    if (!props.open || !snapshot) return;
    const fresh = props.presentation?.slides || [];

    const titleOverrides = {};
    for (const s of slides) {
      const init = snapshot.find((i) => i.id === s.id);
      if (init && (init.title || "") !== (s.title || "")) {
        titleOverrides[s.id] = s.title || "";
      }
    }

    const hasLocalReorder =
      snapshot.length === slides.length &&
      slides.some((s, i) => snapshot[i]?.id !== s.id);

    const freshById = new Map(fresh.map((s) => [s.id, s]));
    const withOverride = (s) => ({
      ...s,
      title:
        titleOverrides[s.id] !== undefined ? titleOverrides[s.id] : s.title,
    });

    let reconciled;
    if (hasLocalReorder) {
      const seen = new Set();
      reconciled = [];
      for (const s of slides) {
        const f = freshById.get(s.id);
        if (!f) continue;
        reconciled.push(withOverride(f));
        seen.add(s.id);
      }
      for (const s of fresh) {
        if (!seen.has(s.id)) reconciled.push(withOverride(s));
      }
    } else {
      reconciled = fresh.map(withOverride);
    }

    setSlides(reconciled);
    setSnapshot(fresh.map((s) => ({ id: s.id, title: s.title || "" })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.presentation?.slides]);

  const isDirty = useMemo(() => {
    if (!snapshot) return false;
    if (snapshot.length !== slides.length) return true;
    return slides.some((s, i) => {
      const init = snapshot[i];
      if (!init) return true;
      return init.id !== s.id || (init.title || "") !== (s.title || "");
    });
  }, [slides, snapshot]);

  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const requestClose = useCallback((afterClose) => {
    if (isDirtyRef.current) {
      pendingActionRef.current = afterClose || null;
      setConfirmCloseOpen(true);
    } else {
      if (afterClose) afterClose();
      propsRef.current.onClose();
    }
  }, []);

  const handleDiscardAndClose = () => {
    setConfirmCloseOpen(false);
    const after = pendingActionRef.current;
    pendingActionRef.current = null;
    if (after) after();
    props.onClose();
  };

  const handleCancelClose = () => {
    pendingActionRef.current = null;
    setConfirmCloseOpen(false);
  };

  // Reset
  useEffect(() => {
    if (!props.open) {
      setSelectedSlides([]);
      setSearch("");
      pendingActionRef.current = null;
      setConfirmCloseOpen(false);
      setShowMoveSlides(false);
      setShowDuplicateSlides(false);
      setShowPublishSlides(false);
      setShowAddSlide(false);
      setAddPosition("");
      setMoveTarget(null);
      setSlidesPosition("");
      setNumberOfDuplicates(1);
      setSlideDuplicateError(false);
      setActionInProgress(false);
      setGridReady(false);
    }
  }, [props.open]);

  // Two rAFs ensures the empty modal has actually painted before we kick off
  // the (synchronous) sortable-grid mount.
  //
  // The timer is a floor under them, not a second attempt at the same thing.
  // Frame callbacks are only delivered while the document is actually
  // rendering, so if the tab is hidden, occluded or throttled when the modal
  // opens, neither frame ever arrives and the grid sits behind the loading
  // row until the tab is looked at again. Closing and reopening does not help
  // - it just queues two more callbacks that are starved the same way. The
  // frames stay the fast path; this guarantees the grid mounts either way.
  useEffect(() => {
    if (!props.open) return undefined;
    let raf1 = 0;
    let raf2 = 0;
    const timer = setTimeout(() => setGridReady(true), 200);
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setGridReady(true));
    });
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [props.open]);

  // Once upstream slides refresh — the signal that an action/change took
  // effect — clear the in-progress indicator and reset any lingering
  // selection. This guarantees selection/action state never carries across a
  // change, regardless of which path triggered it (toolbar action, a saved
  // drag-reorder, etc.).
  useEffect(() => {
    setActionInProgress(false);
    setSelectedSlides([]);
  }, [props.presentation?.slides]);

  // Backstop for the effect above. It clears the flag when upstream slides
  // refresh, which is the signal that an action took effect - but a failed
  // action never refreshes them, so nothing cleared it. That was survivable
  // while this drove a small inline spinner; it now drives a blocking
  // full-screen overlay, which would leave the modal unusable with no way out
  // but a page reload.
  //
  // A timeout rather than an error handler because the actions are
  // fire-and-forget: the parent callbacks return nothing, so there is no
  // failure to catch here, only the absence of a success. Long enough not to
  // cut across a slow publish or a bulk duplicate, and harmless if it does -
  // the work carries on and the slides effect still fires when it lands.
  useEffect(() => {
    if (!actionInProgress) return undefined;
    const timer = setTimeout(() => setActionInProgress(false), 20000);
    return () => clearTimeout(timer);
  }, [actionInProgress]);

  useEffect(() => {
    if (!props.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        requestClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [props.open, requestClose]);

  const filteredSlides = useMemo(() => {
    if (!search.trim()) return slides;
    const q = search.toLowerCase();
    return slides.filter(
      (s) =>
        (s.title || "").toLowerCase().includes(q) ||
        String(s.id).includes(q),
    );
  }, [slides, search]);

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSlides((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  const handleToggleSlide = useCallback((id) => {
    setSelectedSlides((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  }, []);

  const handleRenameSlide = useCallback((slideId, title) => {
    setSlides((prev) =>
      prev.map((s) => (s.id === slideId ? { ...s, title } : s)),
    );
  }, []);

  // Avoid O(N²) findIndex calls during grid rendering.
  const slideIndexById = useMemo(() => {
    const map = new Map();
    slides.forEach((s, i) => map.set(s.id, i));
    return map;
  }, [slides]);
  const slideIndexByIdRef = useRef(slideIndexById);
  slideIndexByIdRef.current = slideIndexById;

  const selectedSlidesSet = useMemo(
    () => new Set(selectedSlides),
    [selectedSlides],
  );

  const handleNavigateToSlide = useCallback((slideId) => {
    const { presentation, navigateTo } = propsRef.current;
    const upstreamSlides = presentation?.slides || [];
    const upstreamIndex = upstreamSlides.findIndex((s) => s.id === slideId);
    const fallbackIndex = slideIndexByIdRef.current.get(slideId) ?? 0;
    navigateTo(upstreamIndex >= 0 ? upstreamIndex : fallbackIndex);
  }, []);

  const handleEditSlide = useCallback((slideId) => {
    requestClose(() => handleNavigateToSlide(slideId));
  }, [requestClose, handleNavigateToSlide]);

  const handleSave = async () => {
    if (!snapshot || saving) return;
    setSaving(true);
    try {
      // Push title changes for every slide whose title has been edited.
      for (const s of slides) {
        const init = snapshot.find((i) => i.id === s.id);
        if (init && (init.title || "") !== (s.title || "")) {
          if (props.onRenameSlide) {
            await Promise.resolve(props.onRenameSlide(s.id, s.title || ""));
          }
        }
      }
      // Push the new order if it changed. Track where the editor's active
      // slide ends up so reveal can re-point at it.
      const orderChanged =
        snapshot.length !== slides.length ||
        slides.some((s, i) => snapshot[i]?.id !== s.id);
      if (orderChanged && props.reorderSlidesQuietly) {
        const activeId = snapshot[props.indexh]?.id;
        const newActiveIndex =
          activeId != null ? slides.findIndex((s) => s.id === activeId) : -1;
        await Promise.resolve(
          props.reorderSlidesQuietly(
            slides.map((s) => s.id),
            newActiveIndex >= 0 ? newActiveIndex : props.indexh,
          ),
        );
      }
      // Re-snapshot so the dirty flag clears.
      setSnapshot(
        slides.map((s) => ({ id: s.id, title: s.title || "" })),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleShowMoveSlideClose = () => {
    setShowMoveSlides(false);
    setMoveTarget(null);
  };

  const handleShowDuplicateSlideClose = () => {
    setShowDuplicateSlides(false);
    setSlideDuplicateError(false);
  };

  const handleShowPublishSlidesClose = () => {
    setShowPublishSlides(false);
  };

  const handleShowAddSlideClose = () => {
    setShowAddSlide(false);
    setAddPosition("");
  };

  // Hand off to the sidebar's existing new-slide dialog at the chosen
  // position. The new slide is inserted *after* the entered slide number,
  // i.e. at array index === that number (1-based number → 0-based "after"
  // index). Closing the management modal lets the sidebar's add dialogs
  // render above the reveal viewer instead of behind this surface.
  const handleProceedAddSlide = () => {
    let pos = parseInt(addPosition, 10);
    if (Number.isNaN(pos)) pos = slides.length;
    pos = Math.max(0, Math.min(slides.length, pos));
    setShowAddSlide(false);
    setAddPosition("");
    requestClose(
      () => props.onRequestAddSlide && props.onRequestAddSlide(pos),
    );
  };

  const handlePublishSlides = () => {
    setActionInProgress(true);
    // Clear on settle rather than waiting for the deck to refresh. Publishing
    // fails outright when the presentation itself is not published yet, and on
    // that path the slides never change, so the refresh effect below never
    // fires and the blocking loader would sit there until the timeout.
    Promise.resolve(
      props.onPublishSlides ? props.onPublishSlides(selectedSlides) : undefined,
    ).finally(() => setActionInProgress(false));
    setSelectedSlides([]);
    handleShowPublishSlidesClose();
  };

  const processNotes = () => {
    const notesObj = {};
    if (selectedSlides.length > 0) {
      selectedSlides.forEach((e) => {
        if (presentationJson?.notes?.hasOwnProperty(e)) {
          notesObj[e] = presentationJson.notes[e];
        }
      });
    }
    return Object.keys(notesObj).length > 0 ? notesObj : null;
  };

  const processAudio = () => {
    const slideAudio = {};
    if (selectedSlides.length > 0) {
      selectedSlides.forEach((e) => {
        if (presentationJson?.slideAudio?.hasOwnProperty(e)) {
          slideAudio[e] = presentationJson.slideAudio[e];
        }
      });
    }
    return Object.keys(slideAudio).length > 0 ? slideAudio : null;
  };

  const handleDuplicateSlides = () => {
    setActionInProgress(true);
    if (props.onDuplicateSlides) {
      props.onDuplicateSlides(
        selectedSlides,
        numberOfDuplicates,
        processNotes(),
        processAudio(),
      );
    }
    setSelectedSlides([]);
    setNumberOfDuplicates(1);
    handleShowDuplicateSlideClose();
    setSlideDuplicateError(false);
  };

  const handleMoveSlides = () => {
    let targetPosition;
    if (moveTarget === "start") {
      targetPosition = 0;
    } else if (moveTarget === "end") {
      targetPosition = slides.length;
    } else {
      targetPosition = parseInt(slidesPosition, 10);
    }
    if (Number.isNaN(targetPosition) || targetPosition < 0) {
      handleShowMoveSlideClose();
      return;
    }
    const clampedTarget = Math.min(slides.length, targetPosition);

    const selected_slides_excluded = [];
    let selectedBeforeTarget = 0;
    const ordered_selected_slides = [];

    slides.forEach((slide, idx) => {
      const isSelected = selectedSlides.indexOf(slide.id) !== -1;
      if (!isSelected) {
        selected_slides_excluded.push(slide.id);
      } else {
        ordered_selected_slides.push(slide.id);
        if (idx < clampedTarget) {
          selectedBeforeTarget++;
        }
      }
    });

    const position = Math.max(0, clampedTarget - selectedBeforeTarget);

    const new_slides = [...selected_slides_excluded];
    new_slides.splice(position, 0, ...ordered_selected_slides);

    const activeSlideId = props.presentation?.slides?.[props.indexh]?.id;
    const newActiveIndex =
      activeSlideId != null ? new_slides.indexOf(activeSlideId) : -1;

    setActionInProgress(true);
    if (props.reorderSlidesQuietly) {
      // Cleared on settle. reorderSlidesQuietly already returns its promise,
      // so unlike the other actions there is nothing to thread through first.
      Promise.resolve(
        props.reorderSlidesQuietly(
          new_slides,
          newActiveIndex >= 0 ? newActiveIndex : position,
        ),
      ).finally(() => setActionInProgress(false));
    }

    setSelectedSlides([]);
    handleShowMoveSlideClose();
  };

  const handleActionChange = (event) => {
    const value = event.target.value;

    // "Add slide" is the one action that doesn't operate on a selection.
    if (value === "add") {
      setAddPosition(String(slides.length));
      setShowAddSlide(true);
      return;
    }

    if (selectedSlides.length === 0) return;

    if (value === "delete") {
      // Parent opens a confirmation modal; don't lock the toolbar or drop the
      // selection until the user confirms and slides actually change — the
      // upstream-refresh effect clears the selection once that happens, so a
      // cancelled delete keeps the selection intact.
      props.onRemoveSlides(selectedSlides);
    }
    if (value === "draft") {
      // Library slides route to a confirmation modal instead of toggling
      // immediately, so only show the in-progress state when at least one
      // regular slide will flip right away.
      const hasRegularSlides = selectedSlides.some((id) => {
        const s = slides.find((slide) => slide.id === id);
        return s && s.initial_presentation === props.presentation.id;
      });
      if (hasRegularSlides) {
        setActionInProgress(true);
      }
      props.toggleStatus(selectedSlides);
      setSelectedSlides([]);
    }
    if (value === "thumbnail") {
      props.generateThumbnails(selectedSlides);
      setSnackbarOpen(true);
      setSelectedSlides([]);
    }
    if (value === "publish") {
      setShowPublishSlides(true);
    }
    if (value === "move") {
      setShowMoveSlides(true);
    }
    if (value === "duplicate") {
      const slide = slides.find((s) => s.id === selectedSlides[0]);
      if (slide) {
        const slideJson = jsonParser(slide.slide_json);
        const isLibrary =
          slide.initial_presentation !== props.presentation.id;
        if (
          slide.prevent_duplicate === "1" ||
          slideJson?.reveal?.slideLayout?.id === "advanced" ||
          isLibrary
        ) {
          setSlideDuplicateError(true);
        }
      }
      setShowDuplicateSlides(true);
    }
  };

  if (!props.open) return null;

  return (
    <>
      <Box
        className="slide-management-modal"
        role="dialog"
        aria-label="Slide management"
      >
        {/* Same full-screen overlay the editor uses while it loads or
            publishes. Saving here rewrites slide order, visibility and
            membership in one go, so the modal has to stop taking input for
            the duration - a spinner inside the Save button left every other
            control live, and this modal is full-screen, so there was nothing
            to signal that the deck behind it was mid-write. The container is
            position:fixed, so the overlay covers exactly the modal. */}
        {(saving || actionInProgress || !gridReady) && (
          <Box
            className="circular-container fs"
            sx={{
              color: "#000",
              flexDirection: "column",
              gap: 2,
              textAlign: "center",
              px: 3,
              backgroundColor: "rgba(255, 255, 255, 1) !important",
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="h6">
              {saving
                ? "Saving…"
                : actionInProgress
                ? "Applying…"
                : "Loading slides…"}
            </Typography>
            <Typography variant="body2">
              {saving
                ? "Slide order, visibility and membership are being written. This window is locked until it finishes."
                : actionInProgress
                ? "The selected slides are being updated."
                : "Fetching the slides in this presentation."}
            </Typography>
          </Box>
        )}

        <AppBar
          className="sm-header"
          sx={{
            position: "relative",
            backgroundColor: "primary.light",
            color: "solid.dark",
          }}
        >
          <DialogContent sx={{ paddingTop: 1, paddingBottom: 1 }}>
            <Toolbar sx={{ justifyContent: "space-between" }}>
              <Row className="w-100">
                <Col className="d-flex align-items-center">
                  <Typography variant="h3" owner="admin" color="adminText.dark">
                    Manage Slides
                  </Typography>
                  <Typography
                    variant="body2"
                    color="adminText.dark"
                    className="sm-header__count ms-3"
                  >
                    {slides.length} slide{slides.length === 1 ? "" : "s"}
                    {selectedSlides.length > 0 &&
                      ` · ${selectedSlides.length} selected`}
                  </Typography>
                </Col>
                <Col className="d-flex justify-content-end align-items-center">
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      owner="admin"
                      dialogbtn="true"
                      dialogvariant="cancel"
                      onClick={() => requestClose()}
                      sx={{
                        marginRight: "10px !important",
                      }}
                    >
                      <Typography
                        variant="button"
                        owner="admin"
                        className="lh"
                      >
                        Close
                      </Typography>
                    </Button>
                    <Button
                      variant="contained"
                      owner="admin"
                      dialogbtn="true"
                      dialogvariant="save"
                      className="lh"
                      onClick={handleSave}
                      disabled={!isDirty || saving}
                      startIcon={
                        saving ? (
                          <CircularProgress
                            size={14}
                            thickness={5}
                            color="inherit"
                          />
                        ) : null
                      }
                    >
                      <Typography
                        variant="button"
                        owner="admin"
                        className="lh"
                      >
                        {saving ? "Saving…" : "Save"}
                      </Typography>
                    </Button>
                  </Stack>
                </Col>
              </Row>
            </Toolbar>
          </DialogContent>
        </AppBar>

        <DialogContent className="sm-body">
                      <Row className="sm-toolbar-row align-items-center">
              <Col className="d-flex align-items-center">
                <FormControl size="small" className="sm-actions-select">
                  <InputLabel sx={{ fontSize: 14 }} id="sm-actions-label">
                    Actions
                  </InputLabel>
                  <Select
                    labelId="sm-actions-label"
                    id="sm-actions"
                    value={selectedAction}
                    label="Actions"
                    disabled={actionInProgress}
                    variant="outlined"
                    onChange={handleActionChange}
                    input={
                      <OutlinedInput
                        sx={{ fontSize: 14 }}
                        label="Actions"
                      />
                    }
                    MenuProps={{ sx: { zIndex: 1500 } }}
                  >
                    <MenuItem value={"add"} sx={{ fontSize: 14 }}>
                      Add Slide(s)
                    </MenuItem>
                    <MenuItem
                      value={"thumbnail"}
                      sx={{ fontSize: 14 }}
                      disabled={selectedSlides.length === 0}
                    >
                      Generate Thumbnail
                    </MenuItem>
                    <MenuItem
                      value={"publish"}
                      sx={{ fontSize: 14 }}
                      disabled={selectedSlides.length === 0}
                    >
                      Publish Slide(s)
                    </MenuItem>
                    <MenuItem
                      value={"draft"}
                      sx={{ fontSize: 14 }}
                      disabled={selectedSlides.length === 0}
                    >
                      Toggle Hide
                    </MenuItem>
                    <MenuItem
                      value={"move"}
                      sx={{ fontSize: 14 }}
                      disabled={selectedSlides.length === 0}
                    >
                      Move Slide(s)
                    </MenuItem>
                    <MenuItem
                      value={"duplicate"}
                      sx={{ fontSize: 14 }}
                      disabled={selectedSlides.length !== 1}
                    >
                      <Tooltip
                        id="duplicate-slide"
                        title="You can only duplicate one slide at a time and a maximum of 10 duplicates."
                        placement="right"
                      >
                        <Box component="span">Duplicate Slide</Box>
                      </Tooltip>
                    </MenuItem>
                    <MenuItem
                      value={"delete"}
                      sx={{ fontSize: 14 }}
                      disabled={selectedSlides.length === 0}
                    >
                      Delete
                    </MenuItem>
                  </Select>
                </FormControl>
              </Col>
              <Col xs={"auto"}>
                <Stack
                  direction={"row"}
                  spacing={2}
                  sx={{ alignItems: "flex-end" }}
                >
                  <TextField
                    id="sm-search"
                    label="Search Slide"
                    className="title-textfield"
                    variant="standard"
                    value={search}
                    size="small"
                    placeholder="Search Slide"
                    onChange={(e) => setSearch(e.target.value)}
                    InputLabelProps={{ style: { fontSize: "14px" } }}
                    InputProps={{
                      style: { fontSize: "14px" },
                      endAdornment: (
                        <InputAdornment position="end">
                          {search.length > 0 ? (
                            <IconButton
                              className="title-clear-btn"
                              sx={{
                                width: "20px",
                                height: "20px",
                                borderRadius: 2,
                              }}
                              onClick={() => setSearch("")}
                              aria-label="Clear search"
                            >
                              <ClearOutlined
                                sx={{ fontSize: "20px" }}
                                className="title-adornment-icon"
                              />
                            </IconButton>
                          ) : (
                            <SearchOutlined
                              sx={{ fontSize: "20px" }}
                              className="title-adornment-icon search"
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: "200px" }}
                  />
                  <Button
                    variant="outlined"
                    className="library-btn slides clear"
                    onClick={() => setSelectedSlides([])}
                    disabled={selectedSlides.length === 0}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="outlined"
                    className="library-btn slides"
                    onClick={() =>
                      setSelectedSlides(filteredSlides.map((s) => s.id))
                    }
                    disabled={
                      filteredSlides.length === 0 ||
                      selectedSlides.length === filteredSlides.length
                    }
                  >
                    Select all
                  </Button>
                </Stack>
              </Col>
            </Row>

          <Container fluid className="slide-management-container">
            {filteredSlides.length === 0 ? (
              <Row className="sm-grid-row">
                <Col xs={12}>
                  <Box className="sm-empty">
                    <Typography variant="body1" color="adminText.main">
                      {search
                        ? "No slides match your search."
                        : "This presentation has no slides yet."}
                    </Typography>
                  </Box>
                </Col>
              </Row>
            ) : !gridReady ? // The overlay above covers this state.
            null : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToWindowEdges]}
              >
                <SortableContext
                  items={filteredSlides.map((s) => s.id)}
                  strategy={rectSortingStrategy}
                >
                  <Row className="sm-grid-row">
                    {filteredSlides.map((slide) => {
                        const originalIndex =
                          slideIndexById.get(slide.id) ?? 0;
                        const isSelected = selectedSlidesSet.has(slide.id);
                        const isLibrarySlide =
                          slide.initial_presentation !==
                          props.presentation.id;
                        return (
                          <SortableSlideCard
                            key={`mgmt-${slide.id}`}
                            id={slide.id}
                            slide={slide}
                            index={originalIndex}
                            isActive={originalIndex === props.indexh}
                            isSelected={isSelected}
                            isLibrarySlide={isLibrarySlide}
                            presentation={props.presentation}
                            thumbnailVersion={
                              props.thumbnailVersions?.[slide.id]
                            }
                            onToggleSelect={handleToggleSlide}
                            onRename={handleRenameSlide}
                            onSelectSlide={handleNavigateToSlide}
                            onEditSlide={handleEditSlide}
                            onPreviewOpenChange={handlePreviewOpenChange}
                            isDragDisabled={
                              selectedSlides.length > 0 || previewOpenCount > 0
                            }
                          />
                        );
                      })}
                    </Row>
                  </SortableContext>
              </DndContext>
            )}
          </Container>
        </DialogContent>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ zIndex: 1450 }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          <Typography variant="body">
            Selected thumbnails will be generated shortly!
          </Typography>
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        aria-labelledby="sm-confirm-close-title"
        sx={{ zIndex: 1450 }}
      >
        <DialogTitle id="sm-confirm-close-title">
          Discard unsaved changes?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved slide changes. If you close now, your edits will
            be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="pb-3 px-3 d-flex justify-content-center align-items-center">
          <Button
            variant="outlined"
            owner="admin"
            dialogbtn="true"
            dialogvariant="cancel"
            onClick={handleCancelClose}
          >
            <Typography variant="button" owner="admin" className="lh">
              Keep editing
            </Typography>
          </Button>
          <Button
            variant="contained"
            owner="admin"
            dialogbtn="true"
            dialogvariant="save"
            className="lh"
            onClick={handleDiscardAndClose}
          >
            <Typography variant="button" owner="admin" className="lh">
              Discard changes
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullScreen={fullScreen}
        open={showMoveSlides}
        onClose={handleShowMoveSlideClose}
        aria-labelledby="sm-move-slide-dialog"
        id="sm-move-slide-dialog"
        maxWidth={"sm"}
        fullWidth={true}
        PaperComponent={DialogPaper}
        sx={{ zIndex: 1450 }}
      >
        <DialogTitle className="d-flex justify-content-center align-items-center dialog-draggable-handle">
          <img className="ca-logo" src={logo} alt="ca-logo" />
          <Typography owner="admin" variant="h5" color="adminText.dark">
            Move Slide(s)
          </Typography>
        </DialogTitle>
        <DialogContent className="pt-3">
          <DialogContentText>
            <Typography
              owner="admin"
              variant="body1"
              color="adminText.main"
              className="pb-3"
            >
              Enter a slide number, or use one of the buttons below.
            </Typography>
          </DialogContentText>
          <Button
            owner="admin"
            variant={moveTarget === "start" ? "contained" : "outlined"}
            dialogbtn="true"
            dialogvariant="primary"
            className="ms-0 me-3"
            onClick={() =>
              setMoveTarget(moveTarget === "start" ? null : "start")
            }
            sx={{ minWidth: 100 }}
          >
            <Typography owner="admin" variant="button" className="lh">
              To start
            </Typography>
          </Button>
          <Button
            owner="admin"
            variant={moveTarget === "end" ? "contained" : "outlined"}
            dialogbtn="true"
            dialogvariant="primary"
            className="ms-0 me-0"
            onClick={() =>
              setMoveTarget(moveTarget === "end" ? null : "end")
            }
            sx={{ minWidth: 100 }}
          >
            <Typography owner="admin" variant="button" className="lh">
              To end
            </Typography>
          </Button>
          <TextField
            autoFocus
            margin="dense"
            id="sm-slides-position"
            className="mt-4"
            label="Move after current slide"
            type="number"
            fullWidth
            variant="outlined"
            value={slidesPosition}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputProps: {
                max: slides.length,
                min: 1,
              },
            }}
            onChange={(event) => {
              setSlidesPosition(event.target.value);
              setMoveTarget(null);
            }}
            onBlur={() => {
              if (slidesPosition > slides.length) {
                setSlidesPosition(slides.length);
              }
              if (slidesPosition !== "" && slidesPosition < 1) {
                setSlidesPosition(1);
              }
            }}
          />
        </DialogContent>
        <DialogActions className="pb-3 px-3 d-flex justify-content-center align-items-center">
          <Button
            variant="outlined"
            owner="admin"
            dialogbtn="true"
            dialogvariant="cancel"
            onClick={handleShowMoveSlideClose}
          >
            <Typography variant="button" owner="admin" className="lh">
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            owner="admin"
            dialogbtn="true"
            dialogvariant="save"
            className="lh"
            onClick={handleMoveSlides}
          >
            <Typography variant="button" owner="admin" className="lh">
              Confirm
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullScreen={fullScreen}
        open={showDuplicateSlides}
        onClose={handleShowDuplicateSlideClose}
        aria-labelledby="sm-duplicate-slide-dialog"
        id="sm-duplicate-slide-dialog"
        maxWidth={"sm"}
        fullWidth={true}
        PaperComponent={DialogPaper}
        sx={{ zIndex: 1450 }}
      >
        <DialogTitle className="d-flex justify-content-center align-items-center dialog-draggable-handle">
          <img className="ca-logo" src={logo} alt="ca-logo" />
          <Typography owner="admin" variant="h5" color="adminText.dark">
            Duplicate Slide
          </Typography>
        </DialogTitle>
        <DialogContent className="pt-3">
          {showSlideDuplicateError ? (
            <DialogContentText>
              <Typography
                owner="admin"
                variant="body1"
                color="adminText.main"
                className="pb-3"
                sx={{ lineHeight: 1.4 }}
              >
                You cannot duplicate this slide. The slide is either a copy
                slide from the library, has been marked as 'disable
                duplicate', or is an Advanced slide using HTML code.
              </Typography>
            </DialogContentText>
          ) : (
            <>
              <DialogContentText>
                <Typography
                  owner="admin"
                  variant="body1"
                  color="adminText.main"
                  className="pb-3"
                >
                  Enter the number of duplicates to make.
                </Typography>
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="sm-number-of-duplicates"
                className="mt-4"
                label="Number of duplicates"
                type="number"
                fullWidth
                variant="outlined"
                value={numberOfDuplicates}
                InputProps={{
                  inputProps: {
                    max: 10,
                    min: 1,
                  },
                }}
                onChange={(event) => {
                  setNumberOfDuplicates(event.target.value);
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions className="pb-3 px-3 d-flex justify-content-center align-items-center">
          <Button
            variant="outlined"
            owner="admin"
            dialogbtn="true"
            dialogvariant="cancel"
            onClick={handleShowDuplicateSlideClose}
          >
            <Typography variant="button" owner="admin" className="lh">
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            owner="admin"
            dialogbtn="true"
            dialogvariant="save"
            className="lh"
            onClick={handleDuplicateSlides}
            disabled={showSlideDuplicateError}
          >
            <Typography variant="button" owner="admin" className="lh">
              Confirm
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullScreen={fullScreen}
        open={showPublishSlides}
        onClose={handleShowPublishSlidesClose}
        aria-labelledby="sm-publish-slides-dialog"
        id="sm-publish-slides-dialog"
        maxWidth={"sm"}
        fullWidth={true}
        PaperComponent={DialogPaper}
        sx={{ zIndex: 1450 }}
      >
        <DialogTitle className="d-flex justify-content-center align-items-center dialog-draggable-handle">
          <img className="ca-logo" src={logo} alt="ca-logo" />
          <Typography owner="admin" variant="h5" color="adminText.dark">
            Publish Slide(s)
          </Typography>
        </DialogTitle>
        <DialogContent className="pt-3">
          <DialogContentText>
            <Typography
              owner="admin"
              variant="body1"
              color="adminText.main"
              className="pb-3"
            >
              Publish the selected slide(s) to the live presentation? This
              updates the published deck without re-publishing the entire
              presentation. The presentation is locked during this process.
            </Typography>
          </DialogContentText>
          <Box className="alert-container">
            <Alert severity="info">
              N.B. The presentation must already be published. To publish
              brand new content, use the full publish first.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions className="pb-3 px-3 d-flex justify-content-center align-items-center">
          <Button
            variant="outlined"
            owner="admin"
            dialogbtn="true"
            dialogvariant="cancel"
            onClick={handleShowPublishSlidesClose}
          >
            <Typography variant="button" owner="admin" className="lh">
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            owner="admin"
            dialogbtn="true"
            dialogvariant="save"
            className="lh"
            onClick={handlePublishSlides}
            disabled={selectedSlides.length === 0}
          >
            <Typography variant="button" owner="admin" className="lh">
              Publish
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullScreen={fullScreen}
        open={showAddSlide}
        onClose={handleShowAddSlideClose}
        aria-labelledby="sm-add-slide-dialog"
        id="sm-add-slide-dialog"
        maxWidth={"sm"}
        fullWidth={true}
        PaperComponent={DialogPaper}
        sx={{ zIndex: 1450 }}
      >
        <DialogTitle className="d-flex justify-content-center align-items-center dialog-draggable-handle">
          <img className="ca-logo" src={logo} alt="ca-logo" />
          <Typography owner="admin" variant="h5" color="adminText.dark">
            Add Slide(s)
          </Typography>
        </DialogTitle>
        <DialogContent className="pt-3">
          <DialogContentText>
            <Typography
              owner="admin"
              variant="body1"
              color="adminText.main"
              className="pb-3"
            >
              Enter the slide number to insert after. The new slide(s) will be
              added directly after this position.
            </Typography>
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="sm-add-slide-position"
            className="mt-4"
            label="Insert after slide number"
            type="number"
            fullWidth
            variant="outlined"
            value={addPosition}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputProps: {
                max: slides.length,
                min: 1,
              },
            }}
            onChange={(event) => {
              setAddPosition(event.target.value);
            }}
            onBlur={() => {
              if (addPosition > slides.length) {
                setAddPosition(String(slides.length));
              }
              if (addPosition !== "" && addPosition < 1) {
                setAddPosition("1");
              }
            }}
          />
        </DialogContent>
        <DialogActions className="pb-3 px-3 d-flex justify-content-center align-items-center">
          <Button
            variant="outlined"
            owner="admin"
            dialogbtn="true"
            dialogvariant="cancel"
            onClick={handleShowAddSlideClose}
          >
            <Typography variant="button" owner="admin" className="lh">
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            owner="admin"
            dialogbtn="true"
            dialogvariant="save"
            className="lh"
            onClick={handleProceedAddSlide}
          >
            <Typography variant="button" owner="admin" className="lh">
              Proceed
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const stopProp = (e) => e.stopPropagation();

const SortableSlideCard = React.memo(function SortableSlideCard(props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.id, disabled: props.isDragDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: props.isDragDisabled
      ? "default"
      : isDragging
        ? "grabbing"
        : "grab",
    touchAction: "none",
  };

  const [previewOpen, setPreviewOpen] = useState(false);

  // Let the manager know a preview is open so it can suspend drag-reordering
  // for the whole grid while the backdrop is up. The cleanup also fires on
  // unmount, so a card removed while previewing won't leave the count stuck.
  const { onPreviewOpenChange } = props;
  useEffect(() => {
    if (!previewOpen) return undefined;
    onPreviewOpenChange?.(true);
    return () => onPreviewOpenChange?.(false);
  }, [previewOpen, onPreviewOpenChange]);

  const isDraft = props.slide.published !== true;

  const handleTitleChange = (e) => {
    if (props.isLibrarySlide) return;
    if (props.onRename) props.onRename(props.id, e.target.value);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        "slide-view-item sm-card d-flex col-4 col-sm-4 col-md-3 col-lg-2 col-xl-1-2 col-xxl-1-2 px-1 pb-4 mb-1 position-relative" +
        (props.isActive ? " active" : "") +
        (isDraft ? " draft" : "") +
        (props.isSelected ? " selected" : "")
      }
      {...attributes}
      {...listeners}
    >
      <div className="sm-card__body">
        <div
          className="ratio ratio-16x9 sm-card__media"
          onClick={() => {
            if (props.onSelectSlide) props.onSelectSlide(props.id);
          }}
        >
          {props.isLibrarySlide && (
            <Tooltip
              title={
                <span
                  className="sm-card__lib-tip"
                  style={{ lineHeight: 1.6 }}
                >
                  <span style={{ display: "block", whiteSpace: "nowrap" }}>
                    P={props.slide.initial_presentation}
                    {props.slide.initial_presentation_title
                      ? ` (${props.slide.initial_presentation_title})`
                      : ""}
                  </span>
                  <span
                    style={{
                      display: "block",
                      whiteSpace: "nowrap",
                      marginTop: 6,
                    }}
                  >
                    S={props.slide.id}
                    {props.slide.title ? ` (${props.slide.title})` : ""}
                  </span>
                </span>
              }
              placement="top"
              componentsProps={{
                tooltip: { sx: { maxWidth: "none" } },
              }}
            >
              <div
                className="library-slide-indicator"
                onClick={stopProp}
              >
                <span className="icon-container">
                  <span className="icon">
                    <NounLink
                      style={{
                        transform: "rotate(45deg)",
                        pointerEvents: "none",
                      }}
                    />
                  </span>
                  <span className="tri" />
                </span>
              </div>
            </Tooltip>
          )}
          {!props.isLibrarySlide && (
            <Tooltip title="Open in editor" placement="top">
              <button
                type="button"
                className="sm-card__edit-btn"
                aria-label="Open slide in editor"
                onClick={(e) => {
                  e.stopPropagation();
                  props.onEditSlide(props.id);
                }}
              >
                <EditOutlined fontSize="small" />
              </button>
            </Tooltip>
          )}
          <Tooltip title="Preview slide" placement="top">
            <button
              type="button"
              className="sm-card__play-btn"
              aria-label="Preview slide"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewOpen(true);
              }}
              onPointerDown={stopProp}
            >
              <PlayArrow fontSize="small" />
            </button>
          </Tooltip>
          {isDraft ? (
            <div className="sm-card__draft-placeholder">
              <span>Slide Hidden</span>
            </div>
          ) : (
            <Thumbnail
              isSlide={true}
              isActive={props.isActive}
              slide={props.slide}
              presentation={props.presentation}
              version={props.thumbnailVersion}
            />
          )}
          <div className="id-container">
            <span className="sm-card__id">{props.slide.id}</span>
          </div>
        </div>
        <div className="sm-card__content">
          <textarea
            className="sm-card__title"
            rows={2}
            placeholder={
              props.isLibrarySlide ? "Library slide" : "Add slide title"
            }
            value={props.slide.title || ""}
            onChange={handleTitleChange}
            disabled={props.isLibrarySlide}
            readOnly={props.isLibrarySlide}
            onClick={stopProp}
            onPointerDown={stopProp}
            onKeyDown={stopProp}
            aria-label="Slide title"
          />
        </div>
      </div>

      <div className="sm-card__footer">
        <span className="sm-card__index">{props.index + 1}</span>
        <span className="sm-card__drag-icon" aria-hidden="true">
          <DragIndicator />
        </span>
        <input
          type="checkbox"
          className="sm-card__check"
          checked={props.isSelected}
          onChange={() => props.onToggleSelect(props.id)}
          onClick={stopProp}
          aria-label="Select slide"
        />
      </div>

      <SlidePreview
        presentationId={props.slide.initial_presentation}
        slideId={props.slide.id}
        mode="view"
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        sx={{ zIndex: 1450 }}
      />
    </div>
  );
});
